import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import {
  ChatConversation,
  ChatMessage,
  ChatMessagesPage,
  ChatParticipant,
  ChatParticipantRole,
  ChatPublicChannel,
} from '../../main/apps/chat/models/chat.models';
import { Gt2ApiService } from './gt2-api.service';

/**
 * Routes du chat — gardées ici (et pas dans ApiRoutes.ts) le temps que le WIP
 * en cours sur ApiRoutes soit committé; repliables ensuite.
 */
class ChatRoutes {
  static CONVERSATIONS = '/v1/chat/conversations';
  static CHANNELS = '/v1/chat/channels';
  static MESSAGES = '/v1/chat/messages/';
  static SEARCH = '/v1/chat/search';
}

/**
 * Accès HTTP pur aux endpoints chat T2/T4 — zéro logique d'état (le state vit
 * dans ChatStore). Réponses typées, enveloppe Laravel `{data}` déballée ici.
 */
@Injectable({ providedIn: 'root' })
export class ChatApiService {
  constructor(
    private http: HttpClient,
    private api: Gt2ApiService,
  ) {}

  private url(path: string): string {
    return this.api.createUrl(path);
  }

  // ---- Conversations ----

  public getConversations(
    options: { archived?: boolean; type?: string; limit?: number } = {},
  ): Observable<ChatConversation[]> {
    const params: string[] = [`limit=${options.limit ?? 100}`];
    if (options.archived) params.push('archived=1');
    if (options.type) params.push(`type=${options.type}`);

    return this.http
      .get<{
        data: ChatConversation[];
      }>(this.url(`${ChatRoutes.CONVERSATIONS}?${params.join('&')}`))
      .pipe(map((r) => r.data));
  }

  /** direct idempotent : le flag `created` vient du statut HTTP (201 vs 200). */
  public createConversation(payload: {
    type: string;
    name?: string;
    description?: string;
    participant_uuid?: string;
    participant_uuids?: string[];
  }): Observable<{ conversation: ChatConversation; created: boolean }> {
    return this.http
      .post<{
        data: ChatConversation;
      }>(this.url(ChatRoutes.CONVERSATIONS), payload, { observe: 'response' })
      .pipe(
        map((response) => ({
          conversation: response.body!.data,
          created: response.status === 201,
        })),
      );
  }

  public getConversation(uuid: string): Observable<ChatConversation> {
    return this.http
      .get<{
        data: ChatConversation;
      }>(this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}`))
      .pipe(map((r) => r.data));
  }

  public updateConversation(
    uuid: string,
    payload: { name?: string; description?: string | null },
  ): Observable<ChatConversation> {
    return this.http
      .patch<{
        data: ChatConversation;
      }>(this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}`), payload)
      .pipe(map((r) => r.data));
  }

  public deleteConversation(uuid: string): Observable<void> {
    return this.http
      .delete(this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}`))
      .pipe(map(() => undefined));
  }

  private conversationAction(
    uuid: string,
    action: string,
  ): Observable<unknown> {
    return this.http.post(
      this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}/${action}`),
      {},
    );
  }

  public archive(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'archive');
  }

  public unarchive(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'unarchive');
  }

  public lock(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'lock');
  }

  public unlock(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'unlock');
  }

  public mute(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'mute');
  }

  public unmute(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'unmute');
  }

  public join(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'join');
  }

  public leave(uuid: string): Observable<unknown> {
    return this.conversationAction(uuid, 'leave');
  }

  public addParticipants(
    uuid: string,
    participantUuids: string[],
  ): Observable<ChatParticipant[]> {
    return this.http
      .post<{ data: ChatParticipant[] }>(
        this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}/participants`),
        {
          participant_uuids: participantUuids,
        },
      )
      .pipe(map((r) => r.data));
  }

  public removeParticipant(uuid: string, userUuid: string): Observable<void> {
    return this.http
      .delete(
        this.url(
          `${ChatRoutes.CONVERSATIONS}/${uuid}/participants/${userUuid}`,
        ),
      )
      .pipe(map(() => undefined));
  }

  public updateParticipantRole(
    uuid: string,
    userUuid: string,
    role: Exclude<ChatParticipantRole, 'owner'>,
  ): Observable<ChatParticipant> {
    return this.http
      .patch<{
        data: ChatParticipant;
      }>(
        this.url(
          `${ChatRoutes.CONVERSATIONS}/${uuid}/participants/${userUuid}`,
        ),
        { role },
      )
      .pipe(map((r) => r.data));
  }

  public markRead(uuid: string, messageUuid: string): Observable<unknown> {
    return this.http.post(
      this.url(`${ChatRoutes.CONVERSATIONS}/${uuid}/read`),
      { message_uuid: messageUuid },
    );
  }

  public getChannels(): Observable<ChatPublicChannel[]> {
    return this.http
      .get<{ data: ChatPublicChannel[] }>(this.url(ChatRoutes.CHANNELS))
      .pipe(map((r) => r.data));
  }

  // ---- Messages ----

  public getMessages(
    conversationUuid: string,
    options: { before?: string; limit?: number } = {},
  ): Observable<ChatMessagesPage> {
    const params: string[] = [`limit=${options.limit ?? 50}`];
    if (options.before) params.push(`before=${options.before}`);

    return this.http
      .get<{
        data: ChatMessage[];
        meta: { custom: { has_more: boolean; next_cursor: string | null } };
      }>(
        this.url(
          `${ChatRoutes.CONVERSATIONS}/${conversationUuid}/messages?${params.join('&')}`,
        ),
      )
      .pipe(
        map((r) => ({
          items: r.data,
          has_more: r.meta?.custom?.has_more ?? false,
          next_cursor: r.meta?.custom?.next_cursor ?? null,
        })),
      );
  }

  /** FormData quand des fichiers sont joints (T6 branchera l'UI d'upload). */
  public sendMessage(
    conversationUuid: string,
    payload: {
      body?: string | null;
      parent_uuid?: string | null;
      files?: File[];
    },
  ): Observable<ChatMessage> {
    const endpoint = this.url(
      `${ChatRoutes.CONVERSATIONS}/${conversationUuid}/messages`,
    );

    if (payload.files?.length) {
      const form = new FormData();
      if (payload.body) form.append('body', payload.body);
      if (payload.parent_uuid) form.append('parent_uuid', payload.parent_uuid);
      payload.files.forEach((file) => form.append('files[]', file, file.name));

      return this.http
        .post<{ data: ChatMessage }>(endpoint, form)
        .pipe(map((r) => r.data));
    }

    return this.http
      .post<{
        data: ChatMessage;
      }>(endpoint, {
        body: payload.body,
        parent_uuid: payload.parent_uuid ?? null,
      })
      .pipe(map((r) => r.data));
  }

  /**
   * Envoi multipart avec progression (T6) — flux HttpEvent complet
   * (HttpEventType.UploadProgress / .Response), précédent event-attached-files.
   */
  public sendMessageWithProgress(
    conversationUuid: string,
    payload: {
      body?: string | null;
      parent_uuid?: string | null;
      files: File[];
    },
  ): Observable<HttpEvent<{ data: ChatMessage }>> {
    const form = new FormData();
    if (payload.body) form.append('body', payload.body);
    if (payload.parent_uuid) form.append('parent_uuid', payload.parent_uuid);
    payload.files.forEach((file) => form.append('files[]', file, file.name));

    return this.http.post<{ data: ChatMessage }>(
      this.url(`${ChatRoutes.CONVERSATIONS}/${conversationUuid}/messages`),
      form,
      { reportProgress: true, observe: 'events' },
    );
  }

  /** Recherche full-text (T7) — scoping participant actif appliqué serveur. */
  public searchMessages(
    q: string,
    conversationUuid?: string | null,
    page = 1,
    limit = 20,
  ): Observable<ChatMessage[]> {
    const params: string[] = [
      `q=${encodeURIComponent(q)}`,
      `page=${page}`,
      `limit=${limit}`,
    ];
    if (conversationUuid) {
      params.push(`conversation=${conversationUuid}`);
    }

    return this.http
      .get<{
        data: ChatMessage[];
      }>(this.url(`${ChatRoutes.SEARCH}?${params.join('&')}`))
      .pipe(map((r) => r.data));
  }

  /** Message unique re-transformé — URLs signées fraîches (TTL 60 min, T6). */
  public getMessage(messageUuid: string): Observable<ChatMessage> {
    return this.http
      .get<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}`))
      .pipe(map((r) => r.data));
  }

  public getPinnedMessages(
    conversationUuid: string,
  ): Observable<ChatMessage[]> {
    return this.http
      .get<{
        data: ChatMessage[];
      }>(
        this.url(
          `${ChatRoutes.CONVERSATIONS}/${conversationUuid}/messages/pinned`,
        ),
      )
      .pipe(map((r) => r.data));
  }

  public editMessage(
    messageUuid: string,
    body: string,
  ): Observable<ChatMessage> {
    return this.http
      .patch<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}`), { body })
      .pipe(map((r) => r.data));
  }

  public deleteMessage(messageUuid: string): Observable<ChatMessage> {
    return this.http
      .delete<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}`))
      .pipe(map((r) => r.data));
  }

  public pinMessage(messageUuid: string): Observable<ChatMessage> {
    return this.http
      .post<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}/pin`), {})
      .pipe(map((r) => r.data));
  }

  public unpinMessage(messageUuid: string): Observable<ChatMessage> {
    return this.http
      .post<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}/unpin`), {})
      .pipe(map((r) => r.data));
  }

  // ---- Réactions (T4) ----

  public addReaction(
    messageUuid: string,
    emoji: string,
  ): Observable<ChatMessage> {
    return this.http
      .post<{
        data: ChatMessage;
      }>(this.url(`${ChatRoutes.MESSAGES}${messageUuid}/reactions`), { emoji })
      .pipe(map((r) => r.data));
  }

  public removeReaction(messageUuid: string, emoji: string): Observable<void> {
    return this.http
      .delete(
        this.url(
          `${ChatRoutes.MESSAGES}${messageUuid}/reactions/${encodeURIComponent(emoji)}`,
        ),
      )
      .pipe(map(() => undefined));
  }
}
